import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, TrendingUp, TrendingDown, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import StreakWidget from "@/components/StreakWidget";
import ForecastCard from "@/components/ForecastCard";
import { DashboardSkeleton } from "@/components/Skeleton";

const scoreColor = (s) => s >= 75 ? "#2A9D8F" : s >= 55 ? "#E9C46A" : "#E06A3B";

export default function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [score, setScore] = useState(null);
  const [move, setMove] = useState(null);
  const [insights, setInsights] = useState([]);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    (async () => {
      const [p, s, m, i] = await Promise.all([
        api.get("/profile"), api.get("/health-score"), api.get("/next-best-move"), api.get("/insights/weekly"),
      ]);
      setProfile(p.data); setScore(s.data); setMove(m.data); setInsights(i.data.insights);
    })();
  }, []);

  const doIt = async () => {
    setExecuting(true);
    try {
      const r = await api.post("/next-best-move/execute");
      toast.success(r.data.message, { description: "This move was scheduled and logged." });
    } finally { setExecuting(false); }
  };

  if (!profile || !score || !move) return <DashboardSkeleton />;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Welcome back</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold">Hey {user?.name?.split(" ")[0]},</h1>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Net Worth</div>
          <div data-testid="accounts-overview-total-net-worth" className="font-mono text-3xl font-bold gold-gradient-text">${profile.net_worth.toLocaleString()}</div>
        </div>
      </div>

      {/* Top row: Health Score + Next Best Move */}
      <div className="grid lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="dashboard-financial-health-score"
          className="glass-card p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Financial Health</div>
              <div className="font-mono text-6xl font-bold" style={{ color: scoreColor(score.overall) }}>
                {score.overall}<span className="text-2xl text-[color:var(--text-muted)]">/100</span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {score.breakdown.map((s) => (
              <div key={s.name} className="flex items-center gap-4">
                <div className="w-36 text-sm text-[color:var(--text-secondary)]">{s.name}</div>
                <div className="flex-1 h-2 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${s.score}%` }} transition={{ duration: 0.8 }}
                    className="h-full rounded-full" style={{ background: scoreColor(s.score) }} />
                </div>
                <div className="font-mono text-sm font-semibold w-10 text-right">{s.score}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-[rgba(212,175,55,0.1)] grid grid-cols-3 gap-4 text-center">
            {score.breakdown.slice(0, 3).map(s => (
              <div key={s.name}><div className="text-xs text-[color:var(--text-muted)]">{s.name}</div><div className="text-xs text-[color:var(--text-secondary)] mt-1">{s.status}</div></div>
            ))}
          </div>
        </motion.div>

        {/* Next Best Move */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          data-testid="next-best-move-card"
          className="glass-card p-8 border-[rgba(224,106,59,0.35)] relative overflow-hidden">
          <div className="absolute inset-0 shimmer pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[rgba(224,106,59,0.12)] text-[color:var(--terracotta)] text-xs uppercase tracking-widest mb-4">
              <Sparkles className="w-3 h-3" /> Next Best Move
            </div>
            <h2 className="font-heading text-2xl font-bold mb-4">{move.action}</h2>
            <div className="space-y-2 mb-6">
              {move.why.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-[color:var(--text-secondary)]">
                  <span className="w-1 h-1 rounded-full bg-[color:var(--gold)] mt-2 shrink-0" /> {w}
                </div>
              ))}
            </div>
            <button data-testid="next-best-move-do-it-button" disabled={executing} onClick={doIt}
              className="gold-btn px-6 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-60">
              {executing ? <><Loader2 className="w-4 h-4 animate-spin" /> Scheduling...</> : <>Do it <ArrowUpRight className="w-4 h-4" /></>}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Middle row: stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Cash", value: profile.cash, color: "#F5EBE6" },
          { label: "Investments", value: profile.investments, color: "#2A9D8F" },
          { label: "Total Debt", value: profile.debt, color: "#E06A3B" },
          { label: "Available after bills", value: profile.available_after_bills, color: "#D4AF37" },
        ].map((s) => (
          <div key={s.label} className="glass-card p-5">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">{s.label}</div>
            <div className="font-mono text-2xl font-bold" style={{ color: s.color }}>${Math.abs(s.value).toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Weekly Insights + Goals preview */}
      <ForecastCard />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold">This week's signal</h3>
            <Link to="/insights" className="text-xs text-[color:var(--gold)] hover:underline">See all</Link>
          </div>
          <div className="space-y-3">
            {insights.slice(0, 4).map((ins, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[rgba(255,255,255,0.02)]">
                {ins.type === "warning" ? <TrendingUp className="w-4 h-4 text-[color:var(--terracotta)] mt-0.5" />
                  : ins.type === "positive" ? <TrendingDown className="w-4 h-4 text-[color:var(--emerald)] mt-0.5" />
                    : <Sparkles className="w-4 h-4 text-[color:var(--gold)] mt-0.5" />}
                <div className="text-sm text-[color:var(--text-primary)]">{ins.text}</div>
              </div>
            ))}
          </div>
        </div>
        <StreakWidget />
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold">Goals in motion</h3>
            <Link to="/goals" className="text-xs text-[color:var(--gold)] hover:underline">Manage</Link>
          </div>
          <div className="space-y-4">
            {profile.goals.slice(0, 3).map((g) => {
              const pct = Math.min(100, (g.current / g.target) * 100);
              return (
                <div key={g.goal_id}>
                  <div className="flex justify-between mb-1 text-sm">
                    <span className="font-medium">{g.title}</span>
                    <span className="font-mono text-[color:var(--text-secondary)]">${g.current.toLocaleString()} / ${g.target.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1 }}
                      className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E9C46A]" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
